import React, { useEffect, useState } from 'react';
import { Plus, Trash2, UserPlus, Users, X } from 'lucide-react';
import { adminApi, errorMessage } from '../api/drive';
import { useAuth } from '../contexts/AuthProvider';
import { useDrive } from '../contexts/DriveProvider';
import { formatDate, initials } from '../lib/files';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import NameDialog from '../components/drive/NameDialog';
import ConfirmDialog from '../components/drive/ConfirmDialog';
import PageHeader, { PageTitle } from '../components/drive/PageHeader';

const LEVELS = {
  member: 'Member',
  admin: 'Admin',
  super_admin: 'Super admin',
};

const LEVEL_HELP = {
  member: 'Uses Drive with the access they are given.',
  admin: 'Also manages people, teams and shared drives.',
  super_admin: 'Also owner of every folder and appoints admins.',
};

const TEAM_COLORS = ['#B4530A', '#0E6B63', '#3B4BA8', '#7A2348', '#4A4740', '#5B2C7A'];

const InviteDialog = ({ open, onOpenChange, onInvited }) => {
  const { toast } = useDrive();
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '' });
  useEffect(() => { if (open) setForm({ email: '', first_name: '', last_name: '' }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.invite(form);
      toast(`${form.email} can now sign in`);
      onInvited();
      onOpenChange(false);
    } catch (err) {
      toast(errorMessage(err, 'Could not add this person.'), 'error');
    }
  };

  const field = (key, label, type = 'text') => (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      <input
        type={type}
        required={key === 'email'}
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="h-10 rounded-lg border border-input bg-background px-3 outline-none focus:border-primary"
      />
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add a person</DialogTitle>
            <DialogDescription>
              Anyone with a lamptwist.be Google account can already sign in. Add someone here to let another address in
              (e.g. a freelancer), or to share with a colleague before their first sign-in.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-5 flex flex-col gap-3">
            {field('email', 'Email', 'email')}
            <div className="grid grid-cols-2 gap-3">
              {field('first_name', 'First name')}
              {field('last_name', 'Last name')}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="h-9 rounded-lg" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="h-9 rounded-lg font-semibold">Add person</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PeopleTab = ({ people, reload, me }) => {
  const { toast } = useDrive();
  const [inviting, setInviting] = useState(false);
  const [filter, setFilter] = useState('');

  const update = async (person, body, ok) => {
    try {
      await adminApi.updatePerson(person.id, body);
      if (ok) toast(ok);
      reload();
    } catch (err) {
      toast(errorMessage(err, 'Could not update this person.'), 'error');
    }
  };

  const shown = people.filter((p) => `${p.name} ${p.email}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <section aria-label="People" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex h-8 min-w-[220px] flex-1 items-center rounded-lg border border-border px-2.5 text-[13.5px] focus-within:border-ring sm:max-w-xs">
          <span className="sr-only">Filter people</span>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name or email" className="w-full bg-transparent outline-none" />
        </label>
        <button type="button" onClick={() => setInviting(true)} className="ml-auto flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-[13px] font-medium text-primary-foreground hover:bg-primary/85">
          <UserPlus className="h-4 w-4" aria-hidden="true" /> Add person
        </button>
      </div>
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-[13.5px]">
          <thead>
            <tr className="border-b border-border text-[13px] font-medium text-muted-foreground">
              <th scope="col" className="py-2.5 pl-4">Person</th>
              <th scope="col" className="py-2.5">Teams</th>
              <th scope="col" className="py-2.5">Last seen</th>
              <th scope="col" className="py-2.5">Rights</th>
              <th scope="col" className="py-2.5 pr-4 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => {
              const isMe = p.id === me?.user_id;
              return (
                <tr key={p.id} className={`border-b border-muted last:border-0 ${p.is_active ? '' : 'opacity-60'}`}>
                  <td className="py-3 pl-4">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold">{initials(p.name)}</span>
                      <div className="flex min-w-0 flex-col">
                        <b className="truncate">{p.name}{isMe && ' (you)'}</b>
                        <span className="truncate text-xs text-muted-foreground">{p.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-1">
                      {p.teams.length === 0 && <span className="text-muted-foreground">—</span>}
                      {p.teams.map((t) => (
                        <span key={t.id} className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs">
                          <span className="h-2 w-2 rounded-sm" style={{ background: t.color }} aria-hidden="true" />{t.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 text-muted-foreground">{p.last_seen ? formatDate(p.last_seen) : 'Never signed in'}</td>
                  <td className="py-3">
                    {me?.is_superuser && !isMe ? (
                      <select
                        aria-label={`Rights for ${p.name}`}
                        value={p.level}
                        onChange={(e) => update(p, { level: e.target.value }, `${p.name} is now ${LEVELS[e.target.value].toLowerCase()}`)}
                        title={LEVEL_HELP[p.level]}
                        className="h-8 rounded-md border border-border bg-background px-1.5 text-[13px]"
                      >
                        {Object.entries(LEVELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    ) : (
                      <span title={LEVEL_HELP[p.level]} className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium">{LEVELS[p.level]}</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-right">
                    {isMe ? <span className="text-muted-foreground">Active</span> : (
                      <button
                        type="button"
                        onClick={() => update(p, { is_active: !p.is_active }, p.is_active ? `${p.name} deactivated` : `${p.name} reactivated`)}
                        className={`h-8 rounded-md px-2.5 text-[13px] font-medium ${p.is_active ? 'text-muted-foreground hover:bg-muted hover:text-foreground' : 'bg-primary text-primary-foreground'}`}
                      >
                        {p.is_active ? 'Deactivate' : 'Reactivate'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Deactivated people are signed out of Drive immediately and their guest links stop working. Their files stay where they are.
      </p>
      <InviteDialog open={inviting} onOpenChange={setInviting} onInvited={reload} />
    </section>
  );
};

const TeamCard = ({ team, people, reload }) => {
  const { toast } = useDrive();
  const [adding, setAdding] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const memberIds = new Set(team.members.map((m) => m.id));
  const candidates = people.filter((p) => p.is_active && !memberIds.has(p.id));

  const run = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast(ok);
      reload();
    } catch (err) {
      toast(errorMessage(err, 'Could not update the team.'), 'error');
    }
  };

  return (
    <li className="flex flex-col gap-3 rounded-xl border border-border p-4">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg text-white" style={{ background: team.color }}>
          <Users className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <button type="button" onClick={() => setRenaming(true)} className="min-w-0 flex-1 truncate rounded text-left text-[14.5px] font-semibold hover:underline" title="Rename team">
          {team.name}
        </button>
        <button type="button" onClick={() => setDeleting(true)} aria-label={`Delete ${team.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="flex gap-1.5" role="group" aria-label="Team colour">
        {TEAM_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={`Colour ${c}`}
            aria-pressed={team.color === c}
            onClick={() => run(() => adminApi.updateTeam(team.id, { color: c }))}
            className={`h-6 w-6 rounded-md ${team.color === c ? 'ring-2 ring-foreground ring-offset-2 ring-offset-card' : ''}`}
            style={{ background: c }}
          />
        ))}
      </div>
      <ul className="flex flex-col">
        {team.members.length === 0 && <li className="py-1 text-sm text-muted-foreground">No members yet.</li>}
        {team.members.map((m) => (
          <li key={m.id} className="flex items-center gap-2 py-1.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-[11px] font-bold">{initials(m.name)}</span>
            <span className="min-w-0 flex-1 truncate text-sm">{m.name}</span>
            <button
              type="button"
              onClick={() => run(() => adminApi.removeMember(team.id, m.id))}
              aria-label={`Remove ${m.name} from ${team.name}`}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
      {candidates.length > 0 && (
        <label className="flex flex-col gap-1">
          <span className="sr-only">Add member to {team.name}</span>
          <select
            value={adding}
            onChange={(e) => {
              const id = Number(e.target.value);
              setAdding('');
              if (id) run(() => adminApi.addMember(team.id, id));
            }}
            className="h-8 rounded-md border border-border bg-background px-1.5 text-[13px]"
          >
            <option value="">+ Add member…</option>
            {candidates.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
          </select>
        </label>
      )}
      <NameDialog
        open={renaming}
        onOpenChange={setRenaming}
        title="Rename team"
        initial={team.name}
        confirmLabel="Rename"
        onSubmit={(name) => run(() => adminApi.updateTeam(team.id, { name }))}
      />
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={`Delete team “${team.name}”?`}
        description="Members keep their own access, but everything shared with this team stops being shared with them."
        confirmLabel="Delete team"
        onConfirm={() => run(() => adminApi.deleteTeam(team.id), 'Team deleted')}
      />
    </li>
  );
};

const TeamsTab = ({ teams, people, reload }) => {
  const { toast } = useDrive();
  const [creating, setCreating] = useState(false);
  return (
    <section aria-label="Teams" className="flex flex-col gap-4">
      <div className="flex items-center">
        <p className="text-sm text-muted-foreground">Share folders and drives with a whole team at once.</p>
        <button type="button" onClick={() => setCreating(true)} className="ml-auto flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-[13px] font-medium text-primary-foreground hover:bg-primary/85">
          <Plus className="h-4 w-4" aria-hidden="true" /> New team
        </button>
      </div>
      {teams.length === 0 && <p className="mt-10 text-center text-[13.5px] text-muted-foreground">No teams yet. Create one to share with a whole group at once.</p>}
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
        {teams.map((t) => <TeamCard key={t.id} team={t} people={people} reload={reload} />)}
      </ul>
      <NameDialog
        open={creating}
        onOpenChange={setCreating}
        title="New team"
        initial=""
        confirmLabel="Create team"
        onSubmit={async (name) => {
          try {
            await adminApi.createTeam({ name, color: TEAM_COLORS[teams.length % TEAM_COLORS.length] });
            reload();
            return true;
          } catch (err) {
            toast(errorMessage(err, 'Could not create the team.'), 'error');
            return false;
          }
        }}
      />
    </section>
  );
};

const PeoplePage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('people');
  const [people, setPeople] = useState(null);
  const [teams, setTeams] = useState([]);

  const reload = () => {
    adminApi.people().then(setPeople).catch(() => setPeople([]));
    adminApi.teams().then(setTeams).catch(() => setTeams([]));
  };
  useEffect(reload, []);

  if (!user?.is_staff) {
    return <p className="p-10 text-center text-muted-foreground">Only admins can manage people and teams.</p>;
  }

  return (
    <div className="flex min-h-screen flex-col">
      <PageHeader>
        <PageTitle>People &amp; teams</PageTitle>
        <div role="tablist" aria-label="People and teams" className="ml-3 flex gap-0.5">
          {[['people', `People${people ? ` ${people.length}` : ''}`], ['teams', `Teams ${teams.length}`]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`h-7 rounded-md px-2.5 text-[13px] font-medium ${tab === key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </PageHeader>
      <div className="flex flex-col gap-4 px-4 py-4 sm:px-6">
      {!people && <div className="h-40 animate-pulse rounded-2xl bg-muted" />}
      {people && tab === 'people' && <PeopleTab people={people} reload={reload} me={user} />}
      {people && tab === 'teams' && <TeamsTab teams={teams} people={people} reload={reload} />}
      </div>
    </div>
  );
};

export default PeoplePage;
